import React, { useState } from 'react';
import { TrendingUp, Mail, Lock, ArrowRight, Eye, EyeOff, RefreshCw, AlertCircle, CheckCircle2, X, AlertTriangle, Shield, Users, Bug, Database, HelpCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import authService from '../services/auth.service';
import { useNavigate } from "react-router-dom";


// Terms Page Component
function TermsPage({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-[#1f2c34] rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl my-8">
        {/* Header */}
        <div className="sticky top-0 bg-[#00a884] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h1 className="text-white text-xl font-medium">Beta Testing Notice</h1>
            <p className="text-white/80 text-sm">Last Updated: January 17, 2026</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Warning Banner */}
          <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-orange-400 font-medium mb-1">Important: This is a Test Version</h2>
              <p className="text-[#8696a0] text-sm">
                Thank you for trying WealthTracker! Please read this before continuing.
              </p>
            </div>
          </div>

          {/* What This Means */}
          <section className="mb-6">
            <h2 className="text-white text-lg font-medium mb-3">What This Means</h2>
            <p className="text-[#8696a0] text-sm leading-relaxed">
              This application is currently <span className="text-white font-medium">in testing phase</span> and is being used to gather feedback and improve functionality. It is <span className="text-white font-medium">not a final product</span>.
            </p>
          </section>

          {/* What You Should Know */}
          <section className="mb-6">
            <h2 className="text-white text-lg font-medium mb-4">What You Should Know</h2>

            {/* About the App */}
            <div className="bg-[#0b141a] rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium mb-2">About the App</h3>
                  <ul className="space-y-2 text-[#8696a0] text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-[#00a884] mt-1">•</span>
                      <span>Features are still being developed and tested</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#00a884] mt-1">•</span>
                      <span>Some features may not work as expected</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#00a884] mt-1">•</span>
                      <span>The app may have bugs or errors</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#00a884] mt-1">•</span>
                      <span>We may make changes at any time without notice</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* About Your Data */}
            <div className="bg-[#0b141a] rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                  <Database className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium mb-2">About Your Data</h3>
                  <ul className="space-y-2 text-[#8696a0] text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-[#00a884] mt-1">•</span>
                      <span>We will do our best to keep your data safe</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#00a884] mt-1">•</span>
                      <span>However, data may be lost during testing or updates</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-400 mt-1">•</span>
                      <span className="text-white font-medium">Please keep your own backup of important financial information</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#00a884] mt-1">•</span>
                      <span>Do not enter any sensitive financial information you cannot afford to lose</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Your Responsibility */}
            <div className="bg-[#0b141a] rounded-lg p-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium mb-2">Your Responsibility</h3>
                  <ul className="space-y-2 text-[#8696a0] text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-[#00a884] mt-1">•</span>
                      <span>This app is for <span className="text-white font-medium">personal testing only</span></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#00a884] mt-1">•</span>
                      <span>Do not use it for making important financial decisions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#00a884] mt-1">•</span>
                      <span>Always verify numbers and calculations yourself</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#00a884] mt-1">•</span>
                      <span>This is <span className="text-white font-medium">not financial advice</span> - consult professionals for financial planning</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* What We're Testing */}
          <section className="mb-6">
            <h2 className="text-white text-lg font-medium mb-3 flex items-center gap-2">
              <Bug className="w-5 h-5 text-[#00a884]" />
              What We're Testing
            </h2>
            <div className="bg-[#0b141a] rounded-lg p-4">
              <p className="text-[#8696a0] text-sm mb-3">We're testing features like:</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#1f2c34] rounded-lg p-3 text-center">
                  <p className="text-white text-sm">Investment Tracking</p>
                </div>
                <div className="bg-[#1f2c34] rounded-lg p-3 text-center">
                  <p className="text-white text-sm">Loan Management</p>
                </div>
                <div className="bg-[#1f2c34] rounded-lg p-3 text-center">
                  <p className="text-white text-sm">Portfolio Calculations</p>
                </div>
                <div className="bg-[#1f2c34] rounded-lg p-3 text-center">
                  <p className="text-white text-sm">User Experience</p>
                </div>
              </div>
            </div>
          </section>

          {/* Your Feedback Helps */}
          <section className="mb-6">
            <div className="bg-[#00a884]/10 border border-[#00a884]/20 rounded-lg p-4">
              <h2 className="text-[#00a884] font-medium mb-2">Your Feedback Helps! 🙏</h2>
              <p className="text-[#8696a0] text-sm mb-3">By using this test version, you help us:</p>
              <div className="space-y-2 text-[#8696a0] text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-[#00a884]">✓</span>
                  <span>Find and fix bugs</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#00a884]">✓</span>
                  <span>Improve features</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#00a884]">✓</span>
                  <span>Make the app better for everyone</span>
                </div>
              </div>
            </div>
          </section>

          {/* No Guarantees */}
          <section className="mb-6">
            <h2 className="text-white text-lg font-medium mb-3">No Guarantees</h2>
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <p className="text-[#8696a0] text-sm mb-2">We cannot guarantee:</p>
              <ul className="space-y-1 text-[#8696a0] text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">×</span>
                  <span>The app will always work</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">×</span>
                  <span>Your data will be perfectly safe</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">×</span>
                  <span>Calculations will always be accurate</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">×</span>
                  <span>The service will continue unchanged</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Use at Your Own Risk */}
          <section className="mb-6">
            <h2 className="text-white text-lg font-medium mb-3">Use at Your Own Risk</h2>
            <p className="text-[#8696a0] text-sm leading-relaxed">
              By using this test app, you understand and accept that this is experimental software, things may not work perfectly, you are testing at your own risk, and we are not responsible for any issues that arise from using this test version.
            </p>
          </section>

          {/* Questions or Issues */}
          <section className="mb-6">
            <div className="bg-[#0b141a] rounded-lg p-4 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <HelpCircle className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-white font-medium mb-2">Questions or Issues?</h2>
                <p className="text-[#8696a0] text-sm mb-2">
                  If you find bugs or have questions, please contact us at:
                </p>
                <a href="mailto:support@utajirihub.com" className="text-[#00a884] text-sm font-medium hover:underline">
                  support@utajirihub.com
                </a>
              </div>
            </div>
          </section>

          {/* Final Note */}
          <div className="bg-[#00a884]/10 border border-[#00a884]/20 rounded-lg p-4 text-center">
            <p className="text-white text-sm mb-2">
              By clicking "I agree," you confirm you understand this is a test version and accept the conditions above.
            </p>
            <p className="text-[#00a884] font-medium">Thank you for being an early tester! 🙏</p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-[#1f2c34] px-6 py-4 border-t border-[#2a3942]">
          <button
            onClick={onClose}
            className="w-full bg-[#00a884] text-white font-medium rounded-lg px-6 py-3 active:bg-[#008f6d] transition-colors"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SignUp({ onSignUp, onSwitchToLogin }) {
  const { register, verifyOTP, loading, error } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [showPromoField, setShowPromoField] = useState(false);

  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otp, setOtp] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendingOtp, setResendingOtp] = useState(false);

  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const navigate = useNavigate();

  // Check promo availability on component mount
  React.useEffect(() => {
    const checkPromo = async () => {
      try {
        const promoStatus = await authService.checkPromoAvailability();
        setShowPromoField(promoStatus.withinBudget === true);
      } catch (err) {
        console.error('Error checking promo availability:', err);
        setShowPromoField(false);
      }
    };

    checkPromo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setLocalSuccess('');

    if (!agreedToTerms) {
      setLocalError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    if (!email || !password) {
      setLocalError('Please fill all fields');
      return;
    }

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters long');
      return;
    }

    const result = await register({
      email,
      password,
      fullName: 'Financial Achiever',
      promoCode: promoCode.trim() || undefined
    });

    if (result.success) {
      setRegisteredEmail(result.data.email);
      setShowOtpScreen(true);
      setOtpSuccess('Please check your email for the verification code');
    } else {
      setLocalError(result.error || 'Registration failed. Please try again.');
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setOtpError('');
    setOtpSuccess('');

    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit code');
      return;
    }

    const result = await verifyOTP(registeredEmail, otp);

    if (result.success) {
      setOtpSuccess('Email verified successfully! Logging you in...');

      // Auto login after successful verification
      // The token is already stored in localStorage by authService.verifyOTP
      setTimeout(() => {
        // Call onSignUp to trigger the parent component to check authentication
        if (onSignUp) {
          onSignUp();
        }
      }, 1000);
    } else {
      setOtpError(result.error || 'Invalid code. Please try again.');
    }
  };

  const handleResendOtp = async () => {
    setResendingOtp(true);
    setOtpError('');
    setOtpSuccess('');
    try {
      await authService.resendOTP(registeredEmail);
      setOtpSuccess('New code sent to your email');
      setOtp('');
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Failed to resend code. Please try again.');
    } finally {
      setResendingOtp(false);
    }
  };

  const handleBackToRegistration = () => {
    setShowOtpScreen(false);
    setOtp('');
    setRegisteredEmail('');
    setOtpError('');
    setOtpSuccess('');
  };

  // OTP Verification Screen
  if (showOtpScreen) {
    return (
      <div className="min-h-screen bg-[#0b141a] flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="bg-[#1f2c34] rounded-3xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-[#00a884] p-6 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-white/20 rounded-2xl mb-3 backdrop-blur-sm">
                <Mail className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-xl font-light text-white mb-2">Verify Your Email</h1>
              <p className="text-xs text-white/80">Code sent to</p>
              <p className="text-xs text-white font-medium mt-1">{registeredEmail}</p>
            </div>

            {/* Form */}
            <div className="p-5">
              {otpSuccess && (
                <div className="mb-4 bg-[#00a884]/10 border border-[#00a884]/20 rounded-lg p-3 flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#00a884] flex-shrink-0 mt-0.5" />
                  <p className="text-[#00a884] text-sm flex-1">{otpSuccess}</p>
                </div>
              )}

              {(error || otpError) && (
                <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-300 text-sm flex-1">{otpError || error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-[#8696a0] text-sm mb-2">Enter Code</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setOtp(value);
                      if (otpError) setOtpError('');
                    }}
                    placeholder="000000"
                    maxLength={6}
                    disabled={loading}
                    className="w-full bg-[#2a3942] text-white text-center text-2xl tracking-[0.5em] placeholder-[#8696a0] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00a884] disabled:opacity-50 border border-[#2a3942]"
                  />
                  <p className="text-[#8696a0] text-xs mt-2 text-center">
                    6-digit code from your email
                  </p>
                </div>

                <button
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length !== 6}
                  className="w-full bg-[#00a884] text-white font-medium rounded-lg px-6 py-3 active:bg-[#008f6d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    'Verify Email'
                  )}
                </button>

                <div className="text-center">
                  <p className="text-[#8696a0] text-sm mb-2">Didn't receive the code?</p>
                  <button
                    onClick={handleResendOtp}
                    disabled={resendingOtp || loading}
                    className="text-[#00a884] hover:underline font-medium text-sm disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${resendingOtp ? 'animate-spin' : ''}`} />
                    {resendingOtp ? 'Sending...' : 'Resend Code'}
                  </button>
                </div>

                <div className="text-center pt-4 border-t border-[#2a3942]">
                  <button
                    onClick={handleBackToRegistration}
                    disabled={loading}
                    className="text-[#8696a0] hover:text-white transition-colors text-sm disabled:opacity-50"
                  >
                    ← Back to Sign Up
                  </button>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-[#8696a0] text-xs mt-4">Code expires in 10 minutes</p>
        </div>
      </div>
    );
  }

  // Registration Form Screen
  return (
    <>
      {showTerms && <TermsPage onClose={() => setShowTerms(false)} />}

      <div className="min-h-screen bg-[#0b141a] flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          {/* Logo/Brand */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00a884] rounded-3xl mb-3 shadow-lg shadow-[#00a884]/20">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-light text-white mb-1">Create Account</h1>
            <p className="text-[#8696a0] text-xs">Start tracking your wealth today</p>
          </div>

          {/* Sign Up Card */}
          <div className="bg-[#1f2c34] rounded-3xl overflow-hidden shadow-2xl">
            <div className="p-5">
              {localSuccess && (
                <div className="mb-4 bg-[#00a884]/10 border border-[#00a884]/20 rounded-lg p-3 flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#00a884] flex-shrink-0 mt-0.5" />
                  <p className="text-[#00a884] text-sm flex-1">{localSuccess}</p>
                </div>
              )}

              {(error || localError) && (
                <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-300 text-sm flex-1">{localError || error}</p>
                </div>
              )}

              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-[#8696a0] text-sm mb-2">Email</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="w-5 h-5 text-[#8696a0]" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (localError) setLocalError('');
                      }}
                      placeholder="you@example.com"
                      disabled={loading}
                      className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00a884] disabled:opacity-50 border border-[#2a3942]"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-[#8696a0] text-sm mb-2">Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="w-5 h-5 text-[#8696a0]" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (localError) setLocalError('');
                      }}
                      placeholder="At least 8 characters"
                      disabled={loading}
                      className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg pl-10 pr-12 py-3 focus:outline-none focus:ring-2 focus:ring-[#00a884] disabled:opacity-50 border border-[#2a3942]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#8696a0] hover:text-white transition-colors"
                      disabled={loading}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Promo Code (Optional) - Only show if within budget */}
                {showPromoField && (
                  <div>
                    <label className="block text-[#8696a0] text-sm mb-2">
                      Promo Code <span className="text-[#8696a0]/60">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase());
                        if (localError) setLocalError('');
                      }}
                      placeholder="Enter code if you have one"
                      disabled={loading}
                      className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00a884] disabled:opacity-50 border border-[#2a3942] uppercase"
                    />
                  </div>
                )}

                {/* Terms */}
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => {
                      setAgreedToTerms(e.target.checked);
                      if (localError) setLocalError('');
                    }}
                    disabled={loading}
                    className="w-4 h-4 mt-0.5 rounded border-[#2a3942] bg-[#2a3942] text-[#00a884] focus:ring-2 focus:ring-[#00a884] focus:ring-offset-0 disabled:opacity-50"
                  />
                  <label className="ml-2 text-[#8696a0] text-sm">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTerms(true)}
                      className="text-[#00a884] hover:underline font-medium"
                    >
                      Terms
                    </button>{' '}
                    and{' '}
                    <button
                      type="button"
                      onClick={() => navigate("/privacy-policy")}
                      className="text-[#00a884] hover:underline font-medium"
                    >
                      Privacy Policy
                    </button>
                  </label>
                </div>

                {/* Sign Up Button */}
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full bg-[#00a884] text-white font-medium rounded-lg px-6 py-3 flex items-center justify-center gap-2 active:bg-[#008f6d] transition-colors shadow-lg shadow-[#00a884]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>

              {/* Sign In Link */}
              <div className="mt-6 text-center pt-6 border-t border-[#2a3942]">
                <p className="text-[#8696a0] text-sm">
                  Already have an account?{' '}
                  <button
                    onClick={onSwitchToLogin}
                    disabled={loading}
                    className="text-[#00a884] hover:underline font-medium disabled:opacity-50"
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </div>
          </div>

          {/* Footer Text */}
          <p className="text-center text-[#8696a0] text-xs mt-4">
            By creating an account, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </>
  );
}



